import { gqlFetchWithAuth } from "utils/fetchData/gqlFetchWithAuth";
import { createProductMut } from "api/mutations/product";
import type {
  CreateProductVariables,
  ProductRawResult,
  ProductStateRawResult,
} from "../product.types";
import type { RunActionReturn } from "../productActions";
import type { FetchError } from "utils/fetchData/types";

export const parseErrorMessage = (
  data: ProductRawResult | ProductStateRawResult | FetchError | Error,
) => {
  if (data instanceof Error) return;
  if (!("type" in data)) return;
  if (data.type !== "graphql") return;

  const gqlMessage = data.error?.response?.errors?.[0]?.message;
  if (!gqlMessage) return;

  const jsonMatch = gqlMessage.match(/details (.*)$/);
  const details = jsonMatch?.[1] ? JSON.parse(jsonMatch[1]) : null;

  const getErrorMessage = () => {
    if (details?.errorMessage) {
      return details?.errorMessage;
    }
    if (details?.argumentException?.[0]?.errorMessage) {
      return details?.argumentException?.[0]?.errorMessage;
    }
    return null;
  };

  const errorMessage = getErrorMessage();
  if (errorMessage) {
    throw new Error(errorMessage);
  }
};

export const sync = async (
  variables: CreateProductVariables | null,
): RunActionReturn => {
  if (!variables) {
    throw new Error("No input variables provided");
  }
  let data;
  try {
    data = await gqlFetchWithAuth({
      query: createProductMut,
      variables: { input: { ...variables } },
    });
    parseErrorMessage(data);
  } catch (err) {
    const error = err as FetchError | Error;
    parseErrorMessage(error);
    throw err;
  }
  if (!data || !data.createProduct || !data.createProduct.result) {
    throw new Error("Product fetch error: Invalid response structure");
  }
  if (data.createProduct.errorMessage) {
    throw new Error(`Product fetch error: ${data.createProduct.errorMessage}`);
  }
  if (!data.createProduct.result.product) {
    throw new Error("Product fetch error: Product data is missing");
  }
  const output = data.createProduct.result;

  return { output };
};
